import React, { useEffect, useState, useContext, useCallback, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { z } from '@/utils/myZodLite'
import { useWizard } from '@/components/mediaBuying/adCreatives/AdCreativeWizard'
import { LeadFormQuestionsView } from './instantForm/LeadFormQuestionsView'
import { useMediaBuyingContext } from '@/context/media-buying-context'
import { usePageData } from '../hooks'

import { SettingsContext } from '@/context/settings-context'
import { QuestionBuilder } from './instantForm/QuestionBuilder'
import { CharLimitedInput } from '@/components/common/CharLimitedInput'
import { SystemQuestionBuilder } from './instantForm/SystemQuestionBuilder'
import { showNotification } from '@/utils/notifications'
import { isEmpty } from 'lodash'
import { useFormValidation } from '../hooks/useFormValidation'
import WizardSteps from '@/components/common/wizard/WizardSteps'

const Navigation = ({ goBack, handleNext }) => {
    return (
        <div className="d-flex gap-2">
            <Button variant="secondary" onClick={goBack} className="tw:w-[156px] tw:!text-sm">
                Back
            </Button>
            <Button className="tw:w-[156px] tw:!text-sm" onClick={handleNext}>
                Next
            </Button>
        </div>
    )
}

export const FormQuestions = () => {
    const {
        goBack,
        goNext,
        getStepContentData,
        setStepContentData,
        registerCustomNavigation,
        clearCustomNavigation,
        dataForStepper,
    } = useWizard()
    const { connectedPublishers } = useMediaBuyingContext()
    const { theme } = useContext(SettingsContext)

    const { pageIcon, pageTitle } = usePageData(connectedPublishers)
    const [questions, setQuestions] = useState([])
    const [contactDescription, setContactDescription] = useState('')
    const [showQuestionWarning, setShowQuestionWarning] = useState(true)
    const [headline, setHeadline] = useState('')
    const [description, setDescription] = useState(null)

    const FormSchema = useMemo(
        () =>
            z.object(
                {
                    questions: z.array(
                        z.object({
                            id: z.string(),
                            question: z.optional(z.string()),
                            type: z.optional(z.string()),
                            options: z.optional(z.array(z.string())),
                            optional: z.optional(z.boolean()),
                        }),
                    ),
                },
                (data) => {
                    const errors = []

                    // Check system questions - must have at least EMAIL
                    const systemQuestions = data.questions.filter(
                        (q) => q.type && q.type !== 'CUSTOM' && !q.question && !q.options,
                    )

                    const hasEmail = systemQuestions.some((q) => q.type === 'EMAIL')
                    if (!hasEmail) {
                        errors.push({
                            message: 'Email is required in additional information.',
                            path: ['systemQuestions'],
                        })
                    }

                    // Check custom questions
                    const customQuestions = data.questions.filter(
                        (q) => q.question || q.type === 'CUSTOM' || (q.options && q.options.length > 0),
                    )

                    customQuestions.forEach((question, index) => {
                        // Question text validation
                        if (isEmpty(question.question)) {
                            errors.push({
                                message: 'Question text is required.',
                                path: [`question_${question.id}_text`],
                            })
                        }

                        // Multiple choice options validation
                        if (question.options && question.options.length > 0) {
                            question.options.forEach((option, optionIndex) => {
                                if (isEmpty(option)) {
                                    errors.push({
                                        message: 'Option text is required.',
                                        path: [`question_${question.id}_option_${optionIndex}`],
                                    })
                                }
                            })
                        }
                    })

                    return errors.length ? errors : true
                },
            ),
        [],
    )

    const { formErrors, validateForm: validate, hasErrors } = useFormValidation(FormSchema)

    const validateForm = useCallback(
        (silent = false) => {
            return validate({ questions }, silent)
        },
        [validate, questions],
    )

    useEffect(() => {
        registerCustomNavigation(
            <Navigation
                goBack={goBack}
                handleNext={() => {
                    if (validate({ questions }, false)) goNext()
                }}
            />,
        )

        return () => clearCustomNavigation()
    }, [questions])

    useEffect(() => {
        const saved = getStepContentData() || {}
        const intro = getStepContentData('formIntro') || {}

        let questions = Array.isArray(saved.questions)
            ? saved.questions.filter((q) => q && typeof q === 'object')
            : []

        // Ensure there's always an EMAIL question by default
        const hasEmailQuestion = questions.some((q) => q.type === 'EMAIL')
        if (!hasEmailQuestion) {
            const emailQuestion = {
                id: `email-${Date.now()}`,
                key: 'email', // Facebook API requires this
                type: 'EMAIL',
                label: 'Email Address', // Facebook API requires this
                optional: false,
            }
            questions = [emailQuestion, ...questions]
        }

        // Migrate existing questions to include Facebook API required fields
        questions = questions.map((question) => {
            const migratedQuestion = { ...question }
            const safeId =
                typeof migratedQuestion.id === 'string' && migratedQuestion.id.trim()
                    ? migratedQuestion.id
                    : crypto.randomUUID()
            const normalizedType = typeof migratedQuestion.type === 'string' ? migratedQuestion.type : ''
            const normalizedQuestion = typeof migratedQuestion.question === 'string' ? migratedQuestion.question : ''
            const normalizedOptions = Array.isArray(migratedQuestion.options)
                ? migratedQuestion.options.map((option) => (typeof option === 'string' ? option : ''))
                : undefined
            const normalizedOptional = typeof migratedQuestion.optional === 'boolean' ? migratedQuestion.optional : false

            if (!migratedQuestion.id || migratedQuestion.id !== safeId) {
                migratedQuestion.id = safeId
            }
            migratedQuestion.question = normalizedQuestion
            migratedQuestion.options = normalizedOptions
            migratedQuestion.optional = normalizedOptional

            // Add key if missing
            if (!migratedQuestion.key) {
                if (normalizedType === 'CUSTOM') {
                    migratedQuestion.key = `custom_${safeId.slice(0, 8)}`
                } else {
                    migratedQuestion.key = normalizedType ? normalizedType.toLowerCase() : 'custom'
                }
            }

            // Add label if missing
            if (!migratedQuestion.label) {
                if (normalizedType === 'CUSTOM') {
                    migratedQuestion.label = migratedQuestion.question || 'Custom Question'
                } else {
                    // Use the same label mapping as SystemQuestionBuilder
                    const labelMap = {
                        EMAIL: 'Email Address',
                        PHONE: 'Phone Number',
                        FULL_NAME: 'Full Name',
                        STREET_ADDRESS: 'Street Address',
                        CITY: 'City',
                        STATE: 'State',
                        PROVINCE: 'Province',
                        COUNTRY: 'Country',
                        POST_CODE: 'Postal Code',
                        ZIP_CODE: 'ZIP Code',
                    }
                    migratedQuestion.label = normalizedType
                        ? labelMap[normalizedType] ||
                          normalizedType.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
                        : migratedQuestion.question || 'Custom Question'
                }
            }

            return migratedQuestion
        })

        // Drop invalid legacy custom placeholders that are not renderable/editable.
        questions = questions.filter(
            (question) =>
                question.type !== 'CUSTOM' ||
                !isEmpty(question.question?.trim?.()) ||
                (Array.isArray(question.options) && question.options.length > 0),
        )

        setQuestions(questions)
        setContactDescription(saved.contactDescription || '')
        setHeadline(intro.headline || '')
        setDescription(intro.description || '')
    }, [])

    useEffect(() => {
        setStepContentData({
            questions,
            contactDescription,
        })
    }, [questions, contactDescription])

    return (
        <div className="tw:flex tw:h-full">
            <WizardSteps adCreative={dataForStepper} currentStep="formQuestions" />
            <div className={`d-flex h-100  ${theme} campaigns-container tw:relative`}>
                <div className="border-right-white-1 tw:pt-4 ">
                    <div className="tw:pl-2 tw:pt-2 pb-4 tw:px-4 border-bottom-white-1 ">
                        Add questions to your form to better understand your leads.
                    </div>

                    <QuestionBuilder
                        questions={questions}
                        onChange={(newQuestions) => {
                            setQuestions(newQuestions)
                            if (hasErrors) {
                                validateForm(true)
                            }
                        }}
                        className="tw:px-3"
                        formErrors={formErrors}
                    />

                    <div className="tw:px-3">Contact Information</div>
                    <div className="tw:px-3 pt-2 tw:text-sm">
                        Ask for contact information and additional personal details. This will be prefilled from their
                        Facebook account.
                    </div>

                    <div className="tw:px-3 pt-4">Description</div>
                    <div className="tw:px-3 pt-2 tw:text-sm">
                        Let people know how the information they give you will be used or shared.
                    </div>

                    <div className="tw:px-3 pt-2">
                        <CharLimitedInput
                            value={contactDescription}
                            onChange={(val) => setContactDescription(val)}
                            placeholder="Enter a message"
                        />
                    </div>

                    <div className="tw:px-3 pt-4">Additional Fields</div>

                    <div>
                        <SystemQuestionBuilder
                            questions={questions}
                            onChange={(newQuestions) => {
                                setQuestions(newQuestions)
                                if (hasErrors) {
                                    validateForm(true)
                                }
                            }}
                        />
                        {!isEmpty(formErrors['systemQuestions']) && (
                            <div className="tw:text-xs tw:text-red-500 tw:pl-3 tw:pt-1">
                                {formErrors['systemQuestions']}
                            </div>
                        )}
                    </div>

                    {showQuestionWarning && (
                        <div className="tw:py-3 tw:mx-4 tw:px-4 border-white-1 border-top-white-1 bg-color-primary tw:relative">
                            <button
                                type="button"
                                onClick={() => setShowQuestionWarning(false)}
                                className="icon tw:py-1 tw:text-gray-200 tw:pl-5 tw:absolute tw:right-4 tw:top-3"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    fill="currentColor"
                                    className="bi bi-x-circle tw:text-gray-200"
                                    viewBox="0 0 16 16"
                                >
                                    <path d="M8 15A7 7 0 1 0 8 1a7 7 0 0 0 0 14zM8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0z" />
                                    <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
                                </svg>
                            </button>
                            <div className="tw:flex">
                                <i className="icon tw:py-1">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="16"
                                        fill="currentColor"
                                        className="bi bi-info-circle tw:text-gray-200"
                                        viewBox="0 0 16 16"
                                    >
                                        <path d="M8 15A7 7 0 1 0 8 1a7 7 0 0 0 0 14zM8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0z" />
                                        <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 .875-.252 1.02-.598l.088-.416c.076-.347.223-.44.548-.525l.088-.38-1.106-.287.738-3.468c.194-.897-.105-1.319-.808-1.319-.545 0-.875.252-1.02.598l-.088.416z" />
                                        <circle cx="8" cy="4.5" r="1" />
                                    </svg>
                                </i>
                                <span className="tw:px-2">Too many questions may reduce form submission rate</span>
                            </div>
                            <div className="tw:pl-6 tw:text-sm tw:text-slate-500 tw:pt-1">
                                Make some questions optional so that it's easy for people to complete and send you the
                                key information that you need.
                            </div>
                        </div>
                    )}
                    <div className="tw:mb-[11rem]"></div>
                </div>

                <div className="tw:position-relative tw:p-6" style={{ width: 320, maxWidth: 320 }}>
                    <LeadFormQuestionsView
                        theme={theme}
                        questions={questions}
                        headline={headline}
                        description={description}
                        pageTitle={pageTitle}
                        pageIcon={pageIcon}
                        hideSelectForm={true}
                    />
                </div>
            </div>
        </div>
    )
}
